/**
 * MarcusGhostCursor — animated cursor that traces Marcus's path across
 * the Ads workspace as he clicks through wizard steps.
 *
 * Ported from SagaGhostCursor with marcus-cursor-* events and Auralis
 * design-system cursor styling.
 * Motion: Quadratic Bézier with perpendicular wobble, asymmetric easing,
 * Gaussian jitter, off-center landing, overshoot on small targets.
 */
import React, { useEffect, useRef, useState } from "react";
import { highlightElement } from "@/shared/lib/ui-highlight";
import { appEvents, type MarcusCursorSource } from "@/shared/lib/events";

export interface MarcusCursorMoveDetail {
  stepId?: string;
  target: string;
  comment?: string;
  pulses?: number;
  durationMs?: number;
  landingPosition?: "top" | "center" | "bottom";
  showTooltip?: boolean;
  source?: MarcusCursorSource;
}

const FADE_AFTER_IDLE_MS = 3000;
const TRAIL_LENGTH = 22;
const POST_LAND_DWELL_MS = 130;
const OVERSHOOT_RETURN_MS = 110;

function gaussianRandom(): number {
  let u = 0;
  let v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
}

function asymmetricEase(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  if (t < 0.6) {
    const tt = t / 0.6;
    return 0.8 * (tt * tt * (3 - 2 * tt));
  }
  const tt = (t - 0.6) / 0.4;
  return 0.8 + 0.2 * (1 - Math.pow(1 - tt, 3));
}

function bezier(
  p0: { x: number; y: number },
  c: { x: number; y: number },
  p1: { x: number; y: number },
  t: number,
) {
  const u = 1 - t;
  return {
    x: u * u * p0.x + 2 * u * t * c.x + t * t * p1.x,
    y: u * u * p0.y + 2 * u * t * c.y + t * t * p1.y,
  };
}

function pickControlPoint(start: { x: number; y: number }, end: { x: number; y: number }) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const mid = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
  const len = distance || 1;
  const perp = { x: -dy / len, y: dx / len };
  const magnitude =
    Math.min(distance * (0.12 + Math.random() * 0.06), 24) * (Math.random() < 0.5 ? -1 : 1);
  const bias = (Math.random() * 0.2 - 0.1) * distance;
  return {
    x: mid.x + perp.x * magnitude + (dx / len) * bias,
    y: mid.y + perp.y * magnitude + (dy / len) * bias,
  };
}

function pickLandingPoint(
  rect: DOMRect,
  hint?: "top" | "center" | "bottom",
): { x: number; y: number } {
  const cx = rect.left + rect.width / 2;
  let cy = rect.top + rect.height / 2;
  if (hint === "top") {
    cy = rect.top + Math.min(rect.height * 0.18, 48);
  } else if (hint === "bottom") {
    cy = rect.bottom - Math.min(rect.height * 0.18, 48);
  }
  if (rect.width < 24 || rect.height < 24) return { x: cx, y: cy };
  const innerW = rect.width * 0.3;
  const innerH = hint === "top" || hint === "bottom" ? rect.height * 0.06 : rect.height * 0.3;
  return {
    x: cx + (Math.random() - 0.5) * innerW,
    y: cy + (Math.random() - 0.5) * innerH,
  };
}

function computeDuration(distance: number, width: number, override?: number): number {
  if (override !== undefined) return Math.max(220, Math.min(1500, override));
  const a = 320;
  const b = 130;
  const fitts = a + b * Math.log2(Math.max(1, distance) / Math.max(8, width) + 1);
  return Math.max(440, Math.min(1100, fitts));
}

interface TrailPoint {
  x: number;
  y: number;
  t: number;
}

export function MarcusGhostCursor() {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [visible, setVisible] = useState(false);
  const [clickPing, setClickPing] = useState(0);
  const [trail, setTrail] = useState<TrailPoint[]>([]);
  const animFrame = useRef<number | null>(null);
  const idleTimer = useRef<number | null>(null);
  const posRef = useRef<{ x: number; y: number } | null>(null);
  posRef.current = pos;

  useEffect(() => {
    const unsubscribe = appEvents.subscribe("marcus-cursor-move-to", (detail) => {
      if (!detail?.target) return;

      const el = document.querySelector<HTMLElement>(`[data-highlight-id="${detail.target}"]`);
      if (!el) {
        highlightElement({
          target: detail.target,
          comment: detail.showTooltip === false ? undefined : detail.comment,
          pulses: detail.pulses,
        });
        return;
      }

      const rect = el.getBoundingClientRect();
      const landing = pickLandingPoint(rect, detail.landingPosition);
      const startPos = posRef.current ?? { x: window.innerWidth - 80, y: window.innerHeight - 80 };
      const dx = landing.x - startPos.x;
      const dy = landing.y - startPos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const duration = computeDuration(
        distance,
        Math.max(rect.width, rect.height),
        detail.durationMs,
      );
      const control = pickControlPoint(startPos, landing);
      const overshootMag = distance > 60 ? Math.min(distance * 0.06, 14) : 0;
      const dirX = distance > 0 ? dx / distance : 0;
      const dirY = distance > 0 ? dy / distance : 0;
      const overshootPoint = {
        x: landing.x + dirX * overshootMag,
        y: landing.y + dirY * overshootMag,
      };

      setVisible(true);
      if (idleTimer.current) window.clearTimeout(idleTimer.current);

      const startTs = performance.now();
      if (animFrame.current) cancelAnimationFrame(animFrame.current);

      const step = (now: number) => {
        const elapsed = now - startTs;
        const t = Math.min(1, elapsed / duration);
        const eased = asymmetricEase(t);
        const p = bezier(startPos, control, overshootMag > 0 ? overshootPoint : landing, eased);
        const jitterScale = t < 0.85 ? 1.0 : 0.3;
        const x = p.x + gaussianRandom() * jitterScale;
        const y = p.y + gaussianRandom() * jitterScale;
        setPos({ x, y });
        setTrail((prev) => [...prev, { x, y, t: now }].slice(-TRAIL_LENGTH));
        if (t < 1) {
          animFrame.current = requestAnimationFrame(step);
        } else if (overshootMag > 0) {
          const correctStart = performance.now();
          const correctFrom = { x: overshootPoint.x, y: overshootPoint.y };
          const correctStep = (n: number) => {
            const u = Math.min(1, (n - correctStart) / OVERSHOOT_RETURN_MS);
            const eu = 1 - Math.pow(1 - u, 3);
            const cx = correctFrom.x + (landing.x - correctFrom.x) * eu;
            const cy = correctFrom.y + (landing.y - correctFrom.y) * eu;
            setPos({ x: cx + gaussianRandom() * 0.4, y: cy + gaussianRandom() * 0.4 });
            if (u < 1) {
              animFrame.current = requestAnimationFrame(correctStep);
            } else {
              landAndClick();
            }
          };
          animFrame.current = requestAnimationFrame(correctStep);
        } else {
          landAndClick();
        }
      };

      const landAndClick = () => {
        const jiggleCount = 3;
        const jiggleEvery = Math.max(28, Math.floor(POST_LAND_DWELL_MS / (jiggleCount + 1)));
        for (let i = 1; i <= jiggleCount; i++) {
          window.setTimeout(() => {
            setPos({
              x: landing.x + gaussianRandom() * 2.5,
              y: landing.y + gaussianRandom() * 2.5,
            });
          }, jiggleEvery * i);
        }
        window.setTimeout(() => {
          setPos({ x: landing.x, y: landing.y });
          setClickPing((n) => n + 1);
          highlightElement({
            target: detail.target,
            comment: detail.showTooltip === false ? undefined : detail.comment,
            pulses: detail.pulses,
          });
          appEvents.publish({
            type: "marcus-cursor-arrived",
            ...(detail.stepId ? { stepId: detail.stepId } : {}),
            target: detail.target,
            ...(detail.comment ? { comment: detail.comment } : {}),
            ...(detail.source ? { source: detail.source } : {}),
          });
        }, POST_LAND_DWELL_MS);
        idleTimer.current = window.setTimeout(() => {
          setVisible(false);
          setTrail([]);
        }, FADE_AFTER_IDLE_MS);
      };

      animFrame.current = requestAnimationFrame(step);
    });

    return () => {
      unsubscribe();
      if (animFrame.current) cancelAnimationFrame(animFrame.current);
      if (idleTimer.current) window.clearTimeout(idleTimer.current);
    };
  }, []);

  if (!visible || !pos) return null;

  return (
    <div
      data-marcus-ghost-cursor="true"
      style={{
        position: "fixed",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 9998,
      }}
      aria-hidden="true"
    >
      <svg
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          overflow: "visible",
        }}
      >
        {trail.length > 1 && (
          <polyline
            points={trail.map((p) => `${p.x},${p.y}`).join(" ")}
            fill="none"
            stroke="var(--marcus-cursor-trail)"
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ filter: "blur(0.35px)" }}
          />
        )}
      </svg>

      <span
        key={clickPing}
        style={{
          position: "absolute",
          left: pos.x - 18,
          top: pos.y - 18,
          width: 36,
          height: 36,
          borderRadius: "50%",
          border: "1.5px solid var(--marcus-cursor-ripple)",
          animation: clickPing > 0 ? "marcus-click-ripple 360ms ease-out" : undefined,
          opacity: 0,
        }}
      />

      <svg
        style={{
          position: "absolute",
          left: pos.x - 5,
          top: pos.y - 5,
          width: 30,
          height: 30,
          filter: "var(--marcus-cursor-filter)",
          transform: "rotate(-7deg)",
          transition: "opacity 200ms ease",
        }}
        viewBox="0 0 32 32"
        fill="none"
      >
        <path
          d="M5.5 4.5 L25.5 14.1 L16.3 16.3 L11.7 26.5 Z"
          fill="var(--marcus-cursor-fill)"
          stroke="var(--marcus-cursor-stroke)"
          strokeWidth={1.7}
          strokeLinejoin="round"
        />
        <path
          d="M16.3 16.3 L12.2 25.2"
          stroke="var(--marcus-cursor-stroke)"
          strokeWidth={1}
          strokeLinecap="round"
          opacity={0.42}
        />
      </svg>

      <span
        style={{
          position: "absolute",
          left: pos.x + 18,
          top: pos.y + 14,
          padding: "3px 9px",
          borderRadius: 999,
          background: "var(--marcus-cursor-label-bg)",
          color: "var(--marcus-cursor-label-fg)",
          border: "1px solid var(--marcus-cursor-label-border)",
          fontSize: 10,
          fontWeight: 600,
          lineHeight: 1,
          letterSpacing: "0.02em",
          textTransform: "uppercase",
          boxShadow: "var(--marcus-cursor-label-shadow)",
          backdropFilter: "blur(20px)",
        }}
      >
        Marcus
      </span>

      <style>{`
        [data-marcus-ghost-cursor] {
          --marcus-cursor-fill: hsl(var(--primary));
          --marcus-cursor-stroke: hsl(var(--surface-container-lowest) / 0.94);
          --marcus-cursor-trail: hsl(var(--primary) / 0.24);
          --marcus-cursor-ripple: hsl(var(--primary) / 0.34);
          --marcus-cursor-label-bg: hsl(var(--primary) / 0.92);
          --marcus-cursor-label-fg: hsl(var(--primary-foreground));
          --marcus-cursor-label-border: hsl(var(--surface-container-lowest) / 0.22);
          --marcus-cursor-filter:
            drop-shadow(0 7px 14px hsl(var(--shadow-color) / calc(var(--shadow-opacity) + 0.28)))
            drop-shadow(0 0 1px hsl(var(--surface-container-lowest) / 0.68));
          --marcus-cursor-label-shadow:
            0 0 40px hsl(var(--shadow-color) / calc(var(--shadow-opacity) + 0.12));
        }

        .dark [data-marcus-ghost-cursor] {
          --marcus-cursor-fill: #ffffff;
          --marcus-cursor-stroke: #1c1b1b;
          --marcus-cursor-trail: rgb(255 255 255 / 0.32);
          --marcus-cursor-ripple: rgb(255 255 255 / 0.42);
          --marcus-cursor-label-bg: rgb(255 255 255 / 0.92);
          --marcus-cursor-label-fg: #1c1b1b;
          --marcus-cursor-label-border: rgb(255 255 255 / 0.18);
          --marcus-cursor-filter:
            drop-shadow(0 8px 18px rgb(0 0 0 / 0.38))
            drop-shadow(0 0 1px rgb(255 255 255 / 0.22));
          --marcus-cursor-label-shadow: 0 0 40px rgb(0 0 0 / 0.24);
        }

        @keyframes marcus-click-ripple {
          0%   { transform: scale(0.6); opacity: 0.65; }
          100% { transform: scale(1.7); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default MarcusGhostCursor;
